import PlanDetails from "@/components/plans/FloatingPlanDetails";
import usePackageLimits from "@/hooks/usePackageLimits";
import {useAgencySeats, useCampaignsLeft} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import {INFINITY_THRESHOLD} from "@/store/constants";
import {isFloatingBuyoutMenuVisibleAtom, isMoreBuyoutsModalOpenAtom} from "@/store/jotaiAtoms";
import type {TFloatingBuyouts} from "@/store/types";
import {useAtomValue} from "jotai";
import {type FC, useEffect, useMemo} from "react";
import {useLocation} from "react-router";
import {useToggle} from "react-use";

const FloatingBuyoutMenu: FC<{
  type?: TFloatingBuyouts;
  className?: string;
}> = ({type = "buyouts", className}) => {
  const isModalOpen = useAtomValue(isMoreBuyoutsModalOpenAtom);
  const isVisibleAtom = useAtomValue(isFloatingBuyoutMenuVisibleAtom);
  const {pathname} = useLocation();
  const {remainingBuyouts} = usePackageLimits();
  const campaignsLeft = useCampaignsLeft();
  const seats = useAgencySeats();
  const isVisible = useMemo(() => {
    const regex =
      /^(\/agency\/\d+\/dashboard\/?|\/agency\/\d+\/subbrand\/\d+\/dashboard\/?|\/company\/\d+\/dashboard\/?)/;

    if (
      (type === "buyouts" && remainingBuyouts < INFINITY_THRESHOLD) ||
      (type === "campaigns" && campaignsLeft! < INFINITY_THRESHOLD) ||
      (type === "seats" && seats < INFINITY_THRESHOLD)
    ) {
      return regex.test(pathname) || isVisibleAtom;
    }

    return false;
  }, [pathname, isVisibleAtom]);
  const [animationTriggerToggle, toggleAnimationTrigger] = useToggle(false);

  useEffect(() => {
    const shouldAnimateOut = isModalOpen.show;
    toggleAnimationTrigger(!shouldAnimateOut);
  }, [isModalOpen.show, toggleAnimationTrigger]);

  if (!isVisible) return null;

  return (
    <div
      className={clsxm(
        `fixed bottom-8 left-8 z-2000 flex origin-bottom-left flex-col rounded-md bg-white transition-all duration-500 ease-in-out ${
          animationTriggerToggle ? "" : "-translate-x-8 translate-y-8 scale-0"
        }`,
        !!campaignsLeft && campaignsLeft < INFINITY_THRESHOLD && type === "buyouts" && "left-48",
        className,
      )}>
      <PlanDetails type={type} />
    </div>
  );
};

export default FloatingBuyoutMenu;
